import React, { useEffect, useState } from 'react';
import { MessageSquareText, Loader2, AlertCircle } from 'lucide-react';
import { MarkdownContent } from './MarkdownContent';
import { moduleApi, type OpenAnswer } from '@/lib/api/moduleApi';

// #293 — what the candidate wrote, for the employer to read.
//
// One component, two mount points, because an employer meets a candidate's
// writing in two situations:
//
//   source="employee"    the onboarding view — an employee working through
//                        modules assigned to them
//   source="application" the applicant view — someone who completed a job's
//                        prerequisite module, read BEFORE the hire decision
//
// The two differ only in which endpoint they call; everything the employer sees
// is identical, which is the point. Two hand-written panels would drift, and the
// drift would show up as one surface quietly not displaying an answer.

type Source =
    | { kind: 'employee'; userId: string }
    | { kind: 'application'; applicationId: string };

interface OpenAnswersPanelProps {
    source: Source;
    /** Rendered above the list. Omit for a bare list inside an existing card. */
    title?: string;
    className?: string;
}

export const OpenAnswersPanel: React.FC<OpenAnswersPanelProps> = ({ source, title, className = '' }) => {
    const [answers, setAnswers] = useState<OpenAnswer[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    // The identity of the thing being fetched, not the object — a new object
    // literal every render would refetch forever.
    const key = source.kind === 'employee' ? `employee:${source.userId}` : `application:${source.applicationId}`;

    useEffect(() => {
        let cancelled = false;
        setAnswers(null);
        setError(null);

        const load = source.kind === 'employee'
            ? moduleApi.getEmployeeOpenAnswers(source.userId)
            : moduleApi.getApplicantOpenAnswers(source.applicationId);

        load
            .then((rows) => { if (!cancelled) setAnswers(rows); })
            .catch(() => {
                if (cancelled) return;
                // Deliberately not silent. An employer who cannot see answers
                // must be told the fetch failed, or they will conclude the
                // candidate wrote nothing — and act on that.
                setError('Kunde inte hämta svaren. Försök igen.');
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    if (error) {
        return (
            <div className={`flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 ${className}`}>
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <p className="text-sm text-gray-700">{error}</p>
            </div>
        );
    }

    if (answers === null) {
        return (
            <div className={`flex items-center gap-2 p-3 text-sm text-gray-500 ${className}`}>
                <Loader2 className="h-4 w-4 animate-spin" /> Hämtar svar…
            </div>
        );
    }

    // A module with no free-text questions renders nothing at all. An empty
    // "Fritextsvar (0)" heading on every applicant would be noise on the many
    // courses that ask none.
    if (answers.length === 0) return null;

    // Group by module so an employer reading several prerequisite modules sees
    // which course each answer came from.
    const byModule = new Map<string, OpenAnswer[]>();
    for (const a of answers) {
        const list = byModule.get(a.module_id);
        if (list) list.push(a);
        else byModule.set(a.module_id, [a]);
    }

    const answeredCount = answers.filter((a) => a.answered).length;

    return (
        <div className={`space-y-4 ${className}`}>
            {title && (
                <div className="flex items-center gap-2">
                    <MessageSquareText className="h-4 w-4 text-[#1A4D4F]" />
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <span className="text-xs text-gray-500">
                        {answeredCount} av {answers.length} besvarade
                    </span>
                </div>
            )}

            {Array.from(byModule.entries()).map(([moduleId, rows]) => (
                <div key={moduleId} className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {rows[0].module_name}
                    </p>
                    <div className="space-y-3">
                        {rows.map((a, i) => (
                            <div key={`${a.module_id}-${a.step_order}-${i}`} className="border-l-2 border-[#A4C3B2] pl-3">
                                <MarkdownContent className="[&_p]:m-0 [&_p]:text-sm [&_p]:font-medium [&_p]:text-gray-900">
                                    {a.question_text || a.step_title}
                                </MarkdownContent>

                                {a.answered ? (
                                    <>
                                        {/* whitespace-pre-wrap, not markdown: this is a
                                            candidate's own words. Rendering it as markdown
                                            would let stray characters restyle the page and
                                            would silently drop line breaks they meant. */}
                                        <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{a.answer_text}</p>
                                        {a.submitted_at && (
                                            <p className="mt-1 text-xs text-gray-400">
                                                Besvarad {new Date(a.submitted_at).toLocaleDateString('sv-SE')}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="mt-1 text-sm italic text-gray-400">Inte besvarad ännu</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
